import { useState, type ReactNode } from "react";
import { ArrowLeft, ArrowRight, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { DisparoLeadFiltersPanel } from "./DisparoLeadFiltersPanel";
import { DisparoLeadList } from "./DisparoLeadList";
import { DisparoSimpleForm } from "./DisparoSimpleForm";
import { DisparoAdvancedForm } from "./DisparoAdvancedForm";
import { DisparoIntegrationSelect } from "./DisparoIntegrationSelect";
import { DisparoSendWindowFields } from "./DisparoSendWindowFields";
import { validateSendWindow, type DisparoSendWindow } from "@/lib/disparos-window";
import { useDisparoLeads, leadToRecipient } from "@/hooks/useDisparoLeads";
import { useDisparos } from "@/hooks/useDisparos";
import type { DisparoLeadFilters, DisparoMessagePayload } from "@/lib/disparos-types";
import { cn } from "@/lib/utils";
import { toast } from "@/components/ui/sonner";

interface DisparoNewWizardProps {
  workspaceId: string;
  onCreated: () => void;
}

const DEFAULT_FILTERS: DisparoLeadFilters = {
  funnelId: null,
  stageSlug: null,
  funnelName: null,
  stageName: null,
  tagIds: [],
  search: "",
};

function Panel({
  title,
  children,
  className,
  headerExtra,
}: {
  title: string;
  children: ReactNode;
  className?: string;
  headerExtra?: ReactNode;
}) {
  return (
    <div className={cn("flex flex-col min-h-0 rounded-lg border bg-card overflow-hidden", className)}>
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b shrink-0">
        <h3 className="text-sm font-medium">{title}</h3>
        {headerExtra}
      </div>
      <div className="flex-1 min-h-0 overflow-hidden p-3">{children}</div>
    </div>
  );
}

export function DisparoNewWizard({ workspaceId, onCreated }: DisparoNewWizardProps) {
  const [step, setStep] = useState(0);
  const [filters, setFilters] = useState<DisparoLeadFilters>(DEFAULT_FILTERS);
  const [mode, setMode] = useState<"simple" | "advanced">("simple");
  const [integrationId, setIntegrationId] = useState("");
  const [campaignName, setCampaignName] = useState("");
  const [message, setMessage] = useState<DisparoMessagePayload>({ type: "text", text: "" });
  const [messagesByLeadId, setMessagesByLeadId] = useState<Record<string, DisparoMessagePayload>>({});
  const [defaultText, setDefaultText] = useState("");
  const [delayMin, setDelayMin] = useState(3);
  const [delayMax, setDelayMax] = useState(8);
  const [scheduledAt, setScheduledAt] = useState("");
  const [windowEnabled, setWindowEnabled] = useState(false);
  const [sendWindow, setSendWindow] = useState<DisparoSendWindow>({
    start: "08:00",
    end: "18:00",
    skip_weekends: true,
  });

  const {
    leads,
    loading,
    selectedIds,
    selectedLeads,
    selectedCount,
    toggleLead,
    selectAll,
    deselectAll,
  } = useDisparoLeads(workspaceId, filters);

  const { createSimple, createAdvanced } = useDisparos(workspaceId);

  const submitting = createSimple.isPending || createAdvanced.isPending;

  const handleSubmit = async () => {
    if (!integrationId) {
      toast.error("Selecione uma conexão WhatsApp");
      return;
    }
    if (!campaignName.trim()) {
      toast.error("Informe o nome da campanha");
      return;
    }
    if (selectedCount === 0) {
      toast.error("Selecione ao menos um lead");
      return;
    }

    const recipients = selectedLeads
      .map(leadToRecipient)
      .filter((r): r is NonNullable<typeof r> => r !== null);

    if (recipients.length === 0) {
      toast.error("Nenhum lead com telefone válido");
      return;
    }

    const scheduleIso = scheduledAt ? new Date(scheduledAt).toISOString() : null;
    if (scheduledAt) {
      const scheduledMs = new Date(scheduledAt).getTime();
      if (Number.isNaN(scheduledMs)) {
        toast.error("Data de agendamento inválida");
        return;
      }
      if (scheduledMs <= Date.now()) {
        toast.error("O agendamento deve ser no futuro");
        return;
      }
    }

    if (windowEnabled) {
      const invalid = validateSendWindow(sendWindow);
      if (invalid) {
        toast.error(invalid);
        return;
      }
    }
    const send_window = windowEnabled ? sendWindow : null;

    if (delayMin > delayMax) {
      toast.error("O delay mínimo não pode ser maior que o máximo");
      return;
    }
    if (mode === "simple") {
      if (message.type === "text" && !message.text?.trim()) {
        toast.error("Digite a mensagem");
        return;
      }
      await createSimple.mutateAsync({
        workspace_id: workspaceId,
        integration_id: integrationId,
        campaign_name: campaignName.trim(),
        message,
        recipients,
        delay_min_seconds: delayMin,
        delay_max_seconds: delayMax,
        scheduled_at: scheduleIso,
        send_window,
      });
    } else {
      const advancedRecipients = recipients.map((r) => ({
        ...r,
        message: messagesByLeadId[r.lead_id] || { type: "text" as const, text: defaultText },
      }));
      const empty = advancedRecipients.filter((r) => !r.message.text?.trim());
      if (empty.length > 0) {
        toast.error(`${empty.length} lead(s) sem mensagem`);
        return;
      }
      await createAdvanced.mutateAsync({
        workspace_id: workspaceId,
        integration_id: integrationId,
        campaign_name: campaignName.trim(),
        recipients: advancedRecipients,
        delay_min_seconds: delayMin,
        delay_max_seconds: delayMax,
        scheduled_at: scheduleIso,
        send_window,
      });
    }

    onCreated();
    setStep(0);
    setCampaignName("");
    setMessage({ type: "text", text: "" });
    setMessagesByLeadId({});
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden gap-3">
      <nav aria-label="Etapas do disparo" className="flex items-center gap-2 text-xs shrink-0">
        <span
          className={cn(
            "transition-colors",
            step === 0 ? "text-foreground font-medium" : "text-muted-foreground",
          )}
        >
          Leads
        </span>
        <span className="text-muted-foreground/40" aria-hidden>
          /
        </span>
        <span
          className={cn(
            "transition-colors",
            step === 1 ? "text-foreground font-medium" : "text-muted-foreground",
          )}
        >
          Configuração
        </span>
      </nav>

      {step === 0 && (
        <div className="flex flex-col flex-1 min-h-0 gap-3 lg:grid lg:grid-cols-[280px_1fr]">
          <Panel
            title="Filtros"
            className="shrink-0 max-h-[min(240px,35vh)] lg:max-h-none lg:min-h-0"
          >
            <div className="h-full overflow-y-auto -m-3 p-3">
              <DisparoLeadFiltersPanel
                workspaceId={workspaceId}
                filters={filters}
                onChange={setFilters}
              />
            </div>
          </Panel>
          <Panel title="Seleção de leads" className="flex-1 min-h-0">
            <div className="flex flex-col h-full min-h-0 -m-3 p-3">
              <DisparoLeadList
                leads={leads}
                filters={filters}
                loading={loading}
                selectedIds={selectedIds}
                selectedCount={selectedCount}
                onToggle={toggleLead}
                onSelectAll={selectAll}
                onDeselectAll={deselectAll}
              />
            </div>
          </Panel>
        </div>
      )}

      {step === 1 && (
        <div className="flex flex-col flex-1 min-h-0 gap-3 lg:grid lg:grid-cols-[1fr_280px] overflow-hidden">
          <Panel
            title={mode === "simple" ? "Disparo simples" : "Disparo avançado"}
            className="flex-1 min-h-0"
            headerExtra={
              <div className="flex items-center gap-1.5 shrink-0">
                <Label htmlFor="mode-toggle" className="text-xs text-muted-foreground font-normal">
                  Simples
                </Label>
                <Switch
                  id="mode-toggle"
                  checked={mode === "advanced"}
                  onCheckedChange={(v) => setMode(v ? "advanced" : "simple")}
                />
                <Label htmlFor="mode-toggle" className="text-xs font-normal">
                  Avançado
                </Label>
              </div>
            }
          >
            <div className="h-full overflow-y-auto -m-3 p-3">
              {mode === "simple" ? (
                <DisparoSimpleForm
                  workspaceId={workspaceId}
                  campaignName={campaignName}
                  onCampaignNameChange={setCampaignName}
                  message={message}
                  onMessageChange={setMessage}
                  delayMin={delayMin}
                  delayMax={delayMax}
                  onDelayMinChange={setDelayMin}
                  onDelayMaxChange={setDelayMax}
                  scheduledAt={scheduledAt}
                  onScheduledAtChange={setScheduledAt}
                />
              ) : (
                <DisparoAdvancedForm
                  campaignName={campaignName}
                  onCampaignNameChange={setCampaignName}
                  leads={selectedLeads}
                  messagesByLeadId={messagesByLeadId}
                  onMessageChange={(id, m) =>
                    setMessagesByLeadId((prev) => ({ ...prev, [id]: m }))
                  }
                  defaultText={defaultText}
                  onDefaultTextChange={setDefaultText}
                  delayMin={delayMin}
                  delayMax={delayMax}
                  onDelayMinChange={setDelayMin}
                  onDelayMaxChange={setDelayMax}
                  scheduledAt={scheduledAt}
                  onScheduledAtChange={setScheduledAt}
                />
              )}
              <DisparoSendWindowFields
                enabled={windowEnabled}
                onEnabledChange={setWindowEnabled}
                value={sendWindow}
                onChange={setSendWindow}
                recipientCount={selectedCount}
                delayMax={delayMax}
                scheduledAt={scheduledAt}
              />
            </div>
          </Panel>
          <Panel title="WhatsApp" className="shrink-0 lg:min-h-0">
            <DisparoIntegrationSelect
              workspaceId={workspaceId}
              value={integrationId}
              onChange={setIntegrationId}
            />
          </Panel>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-1 border-t shrink-0">
        {step > 0 ? (
          <Button variant="outline" size="sm" onClick={() => setStep(0)} disabled={submitting}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            Voltar
          </Button>
        ) : (
          <div />
        )}
        {step === 0 ? (
          <Button size="sm" onClick={() => setStep(1)} disabled={selectedCount === 0}>
            Continuar
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button size="sm" onClick={handleSubmit} disabled={submitting}>
            {submitting ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-1" />
            )}
            Criar disparo
          </Button>
        )}
      </div>
    </div>
  );
}
